import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Dropdown,
    Menu,
    Button,
    Modal,
    message,
    Radio,
    Select,
    Skeleton,
    Progress,
    Icon,
    Popover,
    Badge,
} from "antd";
import styled from "styled-components";
import moment from "moment";
import fileDownload from "js-file-download";

import "moment/locale/pt-br";

import { toBRL } from "../../../components/UI/InputCurrency";
import { request } from "../../../components/Instance";
import * as bankReconciliationActions from "./actions";
import * as bancosActions from "~/screens/Financial/Bancos/actions";
import {
    ModuleHeader,
    ModuleHeaderWrapper,
} from "~/components/ModuleHeader";
import CardList from "./Components/CardList";
import DrawerFilter from "./Components/DrawerFilter";
import DrawerReconcile from "./Components/DrawerReconcile";
import Import from "./Components/Import";
import AutoReconcileDrawer from "./Components/AutoReconcileDrawer";
import ImportsList from "./Components/ImportsList";
import Search from "~/_shared/components/Search";
import SelectionToolbar from "~/_shared/components/SelectionToolbar";
import MonthPicker from "~/_shared/components/MonthPicker";
import FilterPillsBar from "~/_shared/components/FilterPillsBar";
import RestoredFiltersNotice from "~/_shared/components/RestoredFiltersNotice";
import usePersistedFilters from "~/_shared/hooks/usePersistedFilters";
import {
    LEGACY_FILTER_STORAGE_KEY,
    BANK_RECONCILIATION_FILTERS_KEY,
    getDefaultBankReconciliationFilters,
    toBankStatementsRequest,
    isSingleWholeMonth,
    getPeriodLabel,
    isPeriodDefault,
    countActiveFilters,
} from "./helpers";

var Option = Select.Option;

var CardsRow = styled.div`
    display: flex;
    gap: 16px;
    padding-top: 10px;
    margin-bottom: 16px;
`;

var CardWrapper = styled.div`
    flex: 1;
    min-width: 0;
`;

var SummaryCard = styled.div`
    background: #fff;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 10px;
    border: 1px solid #e8e8e8;
    transition: all 0.2s ease;
    height: 100%;
    min-height: 105px;

    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }
`;

var SummaryCardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

var SummaryCardIcon = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${function(props) { return props.bgColor || '#f0f0f0'; }};
    color: ${function(props) { return props.color || '#666'; }};
    font-size: 16px;
`;

var SummaryCardTitle = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 500;
`;

var SummaryCardValue = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    line-height: 1.2;
`;

var SummaryCardSuffix = styled.span`
    font-size: 14px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.45);
    margin-left: 4px;
`;

var ProgressCard = styled.div`
    background: linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%);
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 10px;
    border: 1px solid #d9d9d9;
    height: 100%;
    min-height: 105px;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

var ProgressCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

var ProgressCardTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    font-weight: 500;
`;

var ProgressCardMatches = styled.div`
    font-size: 12px;
    color: #52c41a;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
`;

var BankReconciliationList = function() {
    var dispatch = useDispatch();

    // OPTIMIZED: Separate selectors to prevent unnecessary re-renders
    var data = useSelector(function(state) { return state.bankReconciliation.data; });
    var isLoading = useSelector(function(state) { return state.bankReconciliation.isLoading; });
    var isAutoReconciling = useSelector(function(state) { return state.bankReconciliation.isAutoReconciling; });
    var stats = useSelector(function(state) { return state.bankReconciliation.stats; });
    var drawer = useSelector(function(state) { return state.bankReconciliation.drawer; });

    var bancosData = useSelector(function(state) {
        return state.bancos && state.bancos.data || [];
    });

    var [showFilterDrawer, setShowFilterDrawer] = useState(false);
    var [showImportModal, setShowImportModal] = useState(false);
    var [showAutoReconcileDrawer, setShowAutoReconcileDrawer] = useState(false);
    var [selectedRowKeys, setSelectedRowKeys] = useState([]);

    // Applied filters: kept across refreshes for a few minutes (per tenant/user/page).
    // The redux `filter` (also restored from the IndexedDB cache) is not read here.
    var persisted = usePersistedFilters(BANK_RECONCILIATION_FILTERS_KEY, getDefaultBankReconciliationFilters);
    var applied = persisted.filters;
    var setApplied = persisted.setFilters;
    var defaults = persisted.defaults;
    var activeView = applied.activeView;
    var selectedBankId = applied.selectedBankId;
    var search = applied.search || '';

    var [counts, setCounts] = useState({ pending: 0, reconciled: 0 });

    useEffect(function() {
        // Drop the old shared key so a bank/period picked in another tenant never comes back.
        try { localStorage.removeItem(LEGACY_FILTER_STORAGE_KEY); } catch (e) {}
        if (bancosData.length === 0) {
            dispatch(bancosActions.loadBancos());
        }
    }, []);

    // Stats follow the selected bank (on mount: the restored one).
    useEffect(function() {
        dispatch(bankReconciliationActions.getStats(selectedBankId || null));
    }, [selectedBankId, dispatch]);

    // The only list load: on mount with the restored filters, then whenever they change.
    useEffect(function() {
        // Skip loading bank statements when viewing imports (ImportsList loads its own data)
        if (activeView === 'imports') {
            return;
        }
        dispatch(bankReconciliationActions.loadBankStatements(toBankStatementsRequest(applied)));
    }, [activeView, applied.from, applied.to, selectedBankId, dispatch]);

    var reloadStatements = useCallback(function() {
        dispatch(bankReconciliationActions.loadBankStatements(toBankStatementsRequest(applied)));
    }, [dispatch, applied]);

    // Update counts when data changes
    useEffect(function() {
        if (Array.isArray(data)) {
            var pendingCount = data.filter(function(item) { return item.status === 'pending'; }).length;
            var reconciledCount = data.filter(function(item) { return item.status === 'reconciled'; }).length;
            setCounts({ pending: pendingCount, reconciled: reconciledCount });
        }
    }, [data]);

    // OPTIMIZED: useCallback for handlers to prevent unnecessary re-renders
    var handleViewChange = useCallback(function(view) {
        setApplied({ activeView: view });
        setSelectedRowKeys([]);
    }, [setApplied]);

    var handleReconcile = useCallback(function(record) {
        dispatch(bankReconciliationActions.setDrawer({
            visible: true,
            data: record,
            suggestions: [],
            suggestionsLoaded: false,
        }));
        dispatch(bankReconciliationActions.getSuggestions(record.id, drawer.recordType));
    }, [dispatch, drawer.recordType]);

    var handleRemoveReconciliation = useCallback(function(record) {
        Modal.confirm({
            title: "Remover conciliação?",
            content: "Deseja remover a conciliação deste lançamento?",
            okText: "Sim",
            okType: "danger",
            cancelText: "Cancelar",
            onOk: function() {
                var reconciliation = record.reconciliation;
                if (reconciliation && reconciliation.id) {
                    dispatch(bankReconciliationActions.removeReconciliation(reconciliation.id, record.id));
                }
            },
        });
    }, [dispatch]);

    var handleExportToExcel = useCallback(async function() {
        try {
            var response = await request(
                "POST",
                "/bank-reconciliation/export/excel",
                {
                    from: applied.from,
                    to: applied.to,
                    status: toBankStatementsRequest(applied).status,
                    ids: selectedRowKeys,
                },
                true,
                {
                    responseType: "blob",
                }
            );

            var formattedFrom = moment(applied.from).format("DD-MM-YYYY");
            var formattedTo = moment(applied.to).format("DD-MM-YYYY");

            fileDownload(
                response.data,
                "Conciliacao_" + formattedFrom + "_" + formattedTo + ".xlsx"
            );
        } catch (error) {
            message.error("Não foi possível realizar a exportação!");
        }
    }, [applied, selectedRowKeys]);

    var handleImportSuccess = useCallback(function() {
        setShowImportModal(false);
        // Switch to pending view to see imported items. Changing the view
        // already triggers the load effect; reload only if it won't fire.
        if (activeView === 'pending') {
            reloadStatements();
        } else {
            setApplied({ activeView: 'pending' });
        }
    }, [activeView, reloadStatements, setApplied]);

    var handleBulkDelete = useCallback(function() {
        Modal.confirm({
            title: "Excluir lançamentos selecionados?",
            content: "Você está prestes a excluir " + selectedRowKeys.length + " lançamento(s). Esta ação não pode ser desfeita.",
            okText: "Excluir",
            okType: "danger",
            cancelText: "Cancelar",
            onOk: function() {
                dispatch(bankReconciliationActions.bulkDelete(selectedRowKeys))
                    .then(function() {
                        message.success(selectedRowKeys.length + " lançamento(s) excluído(s) com sucesso");
                        setSelectedRowKeys([]);
                    })
                    .catch(function() {
                        message.error("Erro ao excluir lançamentos");
                    });
            },
        });
    }, [dispatch, selectedRowKeys]);

    var handleAutoReconcile = useCallback(function() {
        // Show the drawer with matching items for review
        setShowAutoReconcileDrawer(true);
    }, []);

    var handleConfirmAutoReconcile = useCallback(function(activeItems) {
        // Only reconcile the statements the user kept in the drawer — pass
        // their ids so excluded items are NOT auto-reconciled on the backend.
        var statementIds = (activeItems || []).map(function(item) { return item.id; });
        dispatch(bankReconciliationActions.autoReconcile(null, statementIds))
            .then(function(response) {
                var count = response.payload.data.data.reconciled || response.payload.data.data.reconciled_count || 0;
                setShowAutoReconcileDrawer(false);
                if (count > 0) {
                    message.success(count + " lançamento(s) conciliado(s) automaticamente");
                    // Reload data and stats (keeping the applied period and bank)
                    reloadStatements();
                    dispatch(bankReconciliationActions.getStats(selectedBankId || null));
                } else {
                    message.info("Nenhum lançamento com correspondência exata encontrado");
                }
            })
            .catch(function() {
                message.error("Erro ao conciliar automaticamente");
            });
    }, [dispatch, reloadStatements, selectedBankId]);

    // OPTIMIZED: useMemo for expensive calculations
    var itemsWithExactMatch = useMemo(function() {
        if (!Array.isArray(data)) return [];
        return data.filter(function(item) {
            return item.status === 'pending' && item.exact_match && item.exact_match.id;
        });
    }, [data]);

    // OPTIMIZED: useMemo for filtered data
    var filtered = useMemo(function() {
        var result = Array.isArray(data) ? data : [];

        // Apply search filter
        if (search) {
            var searchLower = search.toLowerCase();
            result = result.filter(function(item) {
                var description = (item.description || '').toLowerCase();
                var documentNumber = (item.document_number || '').toLowerCase();
                return description.indexOf(searchLower) > -1 || documentNumber.indexOf(searchLower) > -1;
            });
        }

        return result;
    }, [data, search]);

    // OPTIMIZED: useMemo for summary statistics
    var summaryStats = useMemo(function() {
        var credits = 0;
        var debits = 0;
        var pending = 0;
        var reconciled = 0;

        filtered.forEach(function(item) {
            if (item.transaction_type === 'credit') {
                credits += item.amount || 0;
            } else {
                debits += item.amount || 0;
            }
            if (item.status === 'pending') {
                pending++;
            } else if (item.status === 'reconciled') {
                reconciled++;
            }
        });

        return {
            totalCredits: credits,
            totalDebits: debits,
            pendingCount: pending,
            reconciledCount: reconciled,
        };
    }, [filtered]);

    // Keep filteredByStatus as a function for compatibility, but use memoized counts
    var filteredByStatus = useCallback(function(status) {
        return filtered.filter(function(item) {
            return item.status === status;
        });
    }, [filtered]);

    // For backward compatibility with sum() calls
    var totalCredits = useMemo(function() {
        return filtered.filter(function(item) {
            return item.transaction_type === 'credit';
        });
    }, [filtered]);

    var totalDebits = useMemo(function() {
        return filtered.filter(function(item) {
            return item.transaction_type === 'debit';
        });
    }, [filtered]);

    var sum = useCallback(function(array, key) {
        return array.reduce(function(a, b) {
            return a + (b[key] || 0);
        }, 0);
    }, []);

    // OPTIMIZED: Memoized row selection config
    var rowSelection = useMemo(function() {
        return {
            selectedRowKeys: selectedRowKeys,
            onChange: setSelectedRowKeys,
        };
    }, [selectedRowKeys]);

    // OPTIMIZED: Additional memoized handlers
    var handleSearchChange = useCallback(function(e) {
        setApplied({ search: e.target.value });
    }, [setApplied]);

    var handleFilterDrawerOpen = useCallback(function() {
        setShowFilterDrawer(true);
    }, []);

    var handleFilterDrawerClose = useCallback(function() {
        setShowFilterDrawer(false);
    }, []);

    var handleImportModalOpen = useCallback(function() {
        setShowImportModal(true);
    }, []);

    var handleImportModalClose = useCallback(function() {
        setShowImportModal(false);
    }, []);

    var handleAutoReconcileDrawerClose = useCallback(function() {
        setShowAutoReconcileDrawer(false);
    }, []);

    var handleClearSelection = useCallback(function() {
        setSelectedRowKeys([]);
    }, []);

    // Stats and the list reload from their effects (they include the bank).
    var handleBankChange = useCallback(function(value) {
        setApplied({ selectedBankId: value || null });
    }, [setApplied]);

    var handleMonthChange = useCallback(function(value) {
        if (value) {
            var selectedMoment = moment(value, "YYYY-MM");
            setApplied({
                from: selectedMoment.clone().startOf('month').format("YYYY-MM-DD"),
                to: selectedMoment.clone().endOf('month').format("YYYY-MM-DD"),
            });
        }
    }, [setApplied]);

    var handleCustomRange = useCallback(function(from, to) {
        setApplied({ from: from, to: to });
    }, [setApplied]);

    var handleResetPeriod = useCallback(function() {
        setApplied({ from: defaults.from, to: defaults.to });
    }, [defaults, setApplied]);

    var resetFilters = persisted.resetFilters;
    var handleClearFilters = useCallback(function() {
        resetFilters();
        setSelectedRowKeys([]);
    }, [resetFilters]);

    var getCurrentMonth = function() {
        if (applied.from) {
            return moment(applied.from).format("YYYY-MM");
        }
        return moment().format("YYYY-MM");
    };

    var periodIsDefault = isPeriodDefault(applied, defaults);
    var activeFilterCount = countActiveFilters(applied, defaults);

    var VIEW_LABELS = { pending: 'Pendentes', reconciled: 'Conciliados', imports: 'Arquivos' };
    var restoredSummary = (function() {
        var parts = [];
        if (!periodIsDefault) parts.push(getPeriodLabel(applied.from, applied.to));
        if (activeView !== defaults.activeView) parts.push(VIEW_LABELS[activeView] || activeView);
        if (selectedBankId) {
            var bank = bancosData.filter(function(b) { return b.id === selectedBankId; })[0];
            parts.push(bank ? bank.name : 'Banco selecionado');
        }
        if (search) parts.push('"' + search + '"');
        return parts.join(' · ');
    })();

    return (
        <div>
            <DrawerFilter
                initialValues={{ from: applied.from, to: applied.to }}
                visible={showFilterDrawer}
                setVisible={handleFilterDrawerClose}
            />

            <DrawerReconcile />

            <Import
                visible={showImportModal}
                onCancel={handleImportModalClose}
                onSuccess={handleImportSuccess}
            />

            <AutoReconcileDrawer
                visible={showAutoReconcileDrawer}
                onClose={handleAutoReconcileDrawerClose}
                onConfirm={handleConfirmAutoReconcile}
                matchingItems={itemsWithExactMatch}
                isLoading={isAutoReconciling}
            />

            <ModuleHeaderWrapper>
                <ModuleHeader
                    breadcrumbs={["Financeiro", "Conciliação Bancária"]}
                    title="Conciliação Bancária"
                    search={
                        <Search
                            value={search}
                            placeholder="Pesquisar"
                            onChange={handleSearchChange}
                        />
                    }
                    center={
                        <>
                            <Popover
                                trigger="click"
                                placement="bottomRight"
                                content={
                                    <div style={{ width: 320 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                                            <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                                            {!persisted.isDefault && (
                                                <a
                                                    style={{ fontSize: 12, color: '#f5222d' }}
                                                    onClick={handleClearFilters}
                                                >
                                                    Limpar tudo
                                                </a>
                                            )}
                                        </div>

                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visualização</div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {[
                                                    { value: 'pending', label: 'Pendentes', color: '#faad14' },
                                                    { value: 'reconciled', label: 'Conciliados', color: '#52c41a' },
                                                    { value: 'imports', label: 'Arquivos', color: '#1890ff' },
                                                ].map(function(opt) {
                                                    var isActive = activeView === opt.value;
                                                    return (
                                                        <div
                                                            key={opt.value}
                                                            onClick={function() { handleViewChange(opt.value); }}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                                                background: isActive ? opt.color + '0a' : '#fafafa',
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? opt.color : '#d9d9d9', flexShrink: 0 }} />
                                                                <span style={{ fontSize: 12, fontWeight: isActive ? 600 : 400, color: isActive ? opt.color : '#8c8c8c' }}>{opt.label}</span>
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {bancosData.length > 0 && (
                                            <div style={{ marginBottom: 14 }}>
                                                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Banco</div>
                                                <Select
                                                    style={{ width: '100%' }}
                                                    placeholder="Todos os bancos"
                                                    value={selectedBankId || undefined}
                                                    onChange={handleBankChange}
                                                    allowClear
                                                >
                                                    {bancosData.map(function(bank) {
                                                        return (
                                                            <Option key={bank.id} value={bank.id}>
                                                                {bank.name}{bank.bank_code ? ' (' + bank.bank_code + ')' : ''}
                                                            </Option>
                                                        );
                                                    })}
                                                </Select>
                                            </div>
                                        )}

                                        <div style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Período</div>
                                            <MonthPicker
                                                value={getCurrentMonth()}
                                                onChange={handleMonthChange}
                                                onCustomRange={handleCustomRange}
                                                customRangeLabel={
                                                    applied.from && applied.to && !isSingleWholeMonth(applied.from, applied.to)
                                                        ? getPeriodLabel(applied.from, applied.to)
                                                        : null
                                                }
                                                showTodayButton={false}
                                                style={{ display: 'block', width: '100%' }}
                                            />
                                        </div>
                                    </div>
                                }
                            >
                                <Badge
                                    count={activeFilterCount}
                                    offset={[-4, 0]}
                                    style={{ backgroundColor: '#722ed1' }}
                                >
                                    <Button
                                        icon="bars"
                                        style={{ borderRadius: 8 }}
                                    >
                                        Filtros
                                    </Button>
                                </Badge>
                            </Popover>
                            <Dropdown
                                trigger={["click"]}
                                overlay={
                                    <Menu>
                                        {activeView === 'pending' && stats.exact_matches > 0 && (
                                            <Menu.Item
                                                key="reconcile"
                                                onClick={handleAutoReconcile}
                                                disabled={isAutoReconciling}
                                            >
                                                <Icon type="thunderbolt" style={{ color: '#52c41a' }} /> Conciliar {stats.exact_matches} exato(s)
                                            </Menu.Item>
                                        )}
                                        {activeView === 'pending' && stats.exact_matches > 0 && <Menu.Divider />}
                                        <Menu.Item key="import" onClick={handleImportModalOpen}>
                                            <Icon type="upload" /> Importar Extrato
                                        </Menu.Item>
                                        <Menu.Item key="export" onClick={handleExportToExcel}>
                                            <Icon type="file-excel" /> Exportar para Excel
                                        </Menu.Item>
                                    </Menu>
                                }
                            >
                                <Button icon="plus" shape="circle" loading={isAutoReconciling} />
                            </Dropdown>
                        </>
                    }
                    advanced={
                        (function() {
                            var VIEW_COLORS = { pending: '#faad14', reconciled: '#52c41a', imports: '#1890ff' };
                            var isCustomRange = !isSingleWholeMonth(applied.from, applied.to);

                            var pills = [];

                            pills.push({
                                key: 'month', label: getPeriodLabel(applied.from, applied.to),
                                color: isCustomRange ? '#722ed1' : '#1890ff', icon: 'calendar',
                                onRemove: periodIsDefault ? undefined : handleResetPeriod,
                            });

                            pills.push({
                                key: 'view', label: VIEW_LABELS[activeView] || activeView, color: VIEW_COLORS[activeView] || '#595959',
                                onRemove: activeView !== 'pending' ? function() { handleViewChange('pending'); } : undefined,
                            });

                            if (selectedBankId) {
                                var selectedBank = bancosData.filter(function(b) { return b.id === selectedBankId; })[0];
                                var bankLabel = selectedBank ? selectedBank.name : 'Banco selecionado';
                                pills.push({
                                    key: 'bank', label: bankLabel, color: '#13c2c2', icon: 'bank',
                                    onRemove: function() { setApplied({ selectedBankId: null }); },
                                });
                            }

                            if (search) {
                                pills.push({
                                    key: 'search', label: search, color: '#722ed1', icon: 'search',
                                    onRemove: function() { setApplied({ search: '' }); },
                                });
                            }

                            return React.createElement(FilterPillsBar, {
                                pills: pills,
                                onClearAll: persisted.isDefault ? undefined : handleClearFilters,
                            });
                        })()
                    }
                />
            </ModuleHeaderWrapper>

            <RestoredFiltersNotice
                visible={persisted.wasRestored}
                summary={restoredSummary}
                onReset={handleClearFilters}
                onDismiss={persisted.dismissRestored}
            />

            {activeView !== 'imports' && (
                <CardsRow>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#f6ffed" color="#52c41a">
                                            <Icon type="arrow-up" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Total Créditos</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color="#52c41a">
                                        {toBRL(sum(totalCredits, "amount"))}
                                    </SummaryCardValue>
                                </>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#fff1f0" color="#f5222d">
                                            <Icon type="arrow-down" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Total Débitos</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color="#f5222d">
                                        {toBRL(sum(totalDebits, "amount"))}
                                    </SummaryCardValue>
                                </>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#e6f7ff" color="#1890ff">
                                            <Icon type="check-circle" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Conciliados</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color="#1890ff">
                                        {filteredByStatus("reconciled").length}
                                        <SummaryCardSuffix>/ {filtered.length}</SummaryCardSuffix>
                                    </SummaryCardValue>
                                </>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <SummaryCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <>
                                    <SummaryCardHeader>
                                        <SummaryCardIcon bgColor="#fffbe6" color="#faad14">
                                            <Icon type="clock-circle" />
                                        </SummaryCardIcon>
                                        <SummaryCardTitle>Pendentes</SummaryCardTitle>
                                    </SummaryCardHeader>
                                    <SummaryCardValue color="#faad14">
                                        {filteredByStatus("pending").length}
                                    </SummaryCardValue>
                                </>
                            )}
                        </SummaryCard>
                    </CardWrapper>
                    <CardWrapper>
                        <ProgressCard>
                            {isLoading ? (
                                <Skeleton active paragraph={{ rows: 1 }} title={{ width: 100 }} />
                            ) : (
                                <>
                                    <ProgressCardHeader>
                                        <ProgressCardTitle>
                                            <Icon type="dashboard" style={{ color: '#52c41a' }} />
                                            Progresso
                                        </ProgressCardTitle>
                                        {activeView === 'pending' && filteredByStatus('pending').length > 0 && stats.exact_matches > 0 && (
                                            <ProgressCardMatches>
                                                <Icon type="thunderbolt" />
                                                {stats.exact_matches}
                                            </ProgressCardMatches>
                                        )}
                                    </ProgressCardHeader>
                                    <Progress
                                        percent={stats.progress || 0}
                                        strokeColor={{
                                            '0%': '#52c41a',
                                            '100%': '#1890ff',
                                        }}
                                        status={stats.progress === 100 ? 'success' : 'active'}
                                    />
                                    {activeView === 'pending' && filteredByStatus('pending').length > 0 && stats.exact_matches > 0 && (
                                        <div style={{ fontSize: 11, color: '#52c41a', marginTop: 6, textAlign: 'right' }}>
                                            {stats.exact_matches} correspondência(s)
                                        </div>
                                    )}
                                </>
                            )}
                        </ProgressCard>
                    </CardWrapper>
                </CardsRow>
            )}

            {!isLoading && activeView === 'pending' && stats.pending > 0 && filtered.length < stats.pending && (function() {
                var currentPeriod = isSingleWholeMonth(applied.from, applied.to)
                    ? moment(applied.from).format('MMMM [de] YYYY')
                    : moment(applied.from).format('DD/MM/YY') + ' a ' + moment(applied.to).format('DD/MM/YY');
                var otherCount = stats.pending - filtered.length;
                var currentMonth = moment(applied.from).format('YYYY-MM');
                var pendingByMonth = stats.pending_by_month || {};
                var otherMonths = [];
                Object.keys(pendingByMonth).forEach(function(month) {
                    if (month !== currentMonth && pendingByMonth[month] > 0) {
                        otherMonths.push({ month: month, count: pendingByMonth[month] });
                    }
                });
                otherMonths.sort(function(a, b) { return b.month.localeCompare(a.month); });
                var otherMonthsText = otherMonths.map(function(m) {
                    return moment(m.month, 'YYYY-MM').format('MMM/YYYY') + ' (' + m.count + ')';
                }).join(', ');
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 10, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, fontSize: 13, color: '#ad6800' }}>
                        <Icon type="warning" style={{ fontSize: 14, flexShrink: 0 }} />
                        <span>
                            Você está visualizando <strong>{currentPeriod}</strong> com <strong>{filtered.length}</strong> pendente(s).
                            Existem mais <strong>{otherCount}</strong> pendente(s){otherMonthsText ? ': ' + otherMonthsText : ' em outros meses'}.
                        </span>
                    </div>
                );
            })()}

            {activeView === 'imports' ? (
                <ImportsList />
            ) : (
                <CardList
                    filteredData={filtered}
                    isLoading={isLoading}
                    rowSelection={rowSelection}
                    onReconcile={handleReconcile}
                    onRemoveReconciliation={handleRemoveReconciliation}
                    activeView={activeView}
                    onImport={handleImportModalOpen}
                    onChangeDate={handleFilterDrawerOpen}
                />
            )}

            <SelectionToolbar
                count={selectedRowKeys.length}
                onClear={handleClearSelection}
                itemLabel="lançamento"
                actions={[
                    { label: 'Excluir', icon: 'delete', onClick: handleBulkDelete, danger: true }
                ]}
            />
        </div>
    );
};

export default BankReconciliationList;
